import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@/components/Button/Button'
import BulkActionModal from '@/components/common/BulkActionModal'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { toast } from '@/hooks/useToast'

import useRemoveCurriculums from '../mutations/useRemoveCurriculums'
import type { SimpleCurriculum } from '../types/simple-curriculum'

type BulkRemoveCurriculumModalProps = {
  curriculums: SimpleCurriculum[]
  onClose: () => void
  onRemoveSuccess?: (curriculums: SimpleCurriculum[]) => void
}

const PAGE_SIZE = DEFAULT_PAGE_SIZE

const BulkRemoveCurriculumModal = (props: BulkRemoveCurriculumModalProps) => {
  const { t } = useTranslation('curriculumsManagement')
  const [page, setPage] = useState(1)

  const unableToRemove = useMemo(
    () => props.curriculums.filter(curriculum => !curriculum.canBeDeleted),
    [props.curriculums]
  )

  const unableToRemoveCount = unableToRemove.length

  const paginatedUnableToRemove = useMemo(() => {
    const startIdx = (page - 1) * PAGE_SIZE
    const endIdx = startIdx + PAGE_SIZE

    return unableToRemove.slice(startIdx, endIdx)
  }, [page, unableToRemove])

  const { mutate: remove, isPending: isRemovePending } = useRemoveCurriculums({
    onSuccess: response => {
      if (response.successfully_deleted > 0) {
        props.onRemoveSuccess?.(props.curriculums)
        toast({
          variant: 'success',
          title: t('toast.curricula-were-removed', {
            count: response.successfully_deleted
          })
        })
      }

      if (response.unsuccessfully_deleted > 0) {
        toast({
          variant: 'error',
          title: t('toast.failed-to-remove-curricula', {
            count: response.unsuccessfully_deleted
          })
        })
      }

      props.onClose()
    },
    onError: () => {
      toast({
        variant: 'error',
        title: t('toast.curricula-remove-error')
      })
    }
  })

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) setPage(1)
    props.onClose()
  }

  const handleRemoveClick = () =>
    remove(props.curriculums.map(curriculum => curriculum.id))

  return (
    <BulkActionModal
      open
      onOpenChange={handleOpenChange}
      count={unableToRemoveCount}
      unableToPerformActionData={paginatedUnableToRemove}
      page={page}
      pageSize={PAGE_SIZE}
      onPageChange={setPage}
      title={t('header.remove-curricula')}
      description={
        <>
          <p>
            {t('help.want-to-remove-curricula', {
              count: props.curriculums.length
            })}
          </p>
          <p>{t('help.contact-with-admin')}</p>
        </>
      }
      alertTitle={t('alert.unable-to-remove-curricula.title', {
        count: unableToRemoveCount
      })}
      alertDescription={t('alert.unable-to-remove-curricula.description')}
      confirmButton={
        <Button
          variant="danger"
          onClick={handleRemoveClick}
          loading={isRemovePending}
        >
          {t('button.remove')}
        </Button>
      }
    />
  )
}

export default BulkRemoveCurriculumModal
